import { CANONICAL_PLATFORMS, createCanonicalDataset, normalizeTimestamp } from '@canonical-process';
import { INGESTION_SOURCE_TYPES, createIngestionResult, defineSourceAdapter, SOURCE_ADAPTER_TYPES } from '@ingestion-core';

const CANDIDATES = {
  caseId: ['case_id','caseid','ticket_id','ticketid','issue_id','issueid','task_id','sys_id','id','key','number','issue_key'],
  caseKey: ['case_key','casekey','issue_key','issuekey','key','number','ticket','ticket_number','task_number'],
  activity: ['activity','activity_name','status','state','transition','event','event_name'],
  timestamp: ['timestamp','event_time','event_timestamp','changed_at','created_at','sys_created_on','datetime','date_time'],
  resource: ['resource','user','actor','author','assignee','assigned_to','agent'],
  summary: ['summary','short_description','description','title','subject'],
  createdAt: ['case_created_at','created_at','opened_at','create_time','created'],
  closedAt: ['case_closed_at','closed_at','resolved_at','close_time','closed'],
};

const normalizeHeader = value => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
const nonEmpty = (...values) => values.find(v => v !== undefined && v !== null && String(v).trim() !== '') ?? '';

export function inferCsvMapping(headers = []) {
  const normalized = headers.map(h => ({ raw: h, normalized: normalizeHeader(h) }));
  const mapping = {};
  Object.entries(CANDIDATES).forEach(([role, candidates]) => {
    const exact = normalized.find(h => candidates.includes(h.normalized));
    if (exact) mapping[role] = exact.raw;
  });
  if (!mapping.caseKey && mapping.caseId) mapping.caseKey = mapping.caseId;
  return mapping;
}

export function defaultColumnRoles(headers = [], mapping = {}) {
  const used = new Set(Object.values(mapping).filter(Boolean));
  return Object.fromEntries(headers.map(header => [header, used.has(header) ? 'mapped' : 'case-attribute']));
}

function buildDefinitions(columns, prefix, attributeNotes = {}) {
  return columns.map((column, index) => ({
    id: `${prefix}${index}`,
    key: `${prefix}${index}`,
    name: column,
    sourceField: column,
    dataType: 'string',
    description: String(attributeNotes?.[column] || ''),
  }));
}

function collectAttributeValues(row, columns, prefix) {
  const out = {};
  columns.forEach((column, index) => { out[`${prefix}${index}`] = row?.[column] ?? ''; });
  return out;
}

export function ingestCsvEventRows({ rows = [], headers = [], mapping = {}, columnRoles = {}, attributeNotes = {}, model = {}, maxCases = 50000 } = {}) {
  const diagnostics = [];
  const required = ['caseId', 'activity', 'timestamp'];
  for (const key of required) {
    if (!mapping[key]) diagnostics.push({ severity: 'error', code: 'missing-mapping', message: `Map a CSV column to ${key}.` });
  }

  const mappedColumns = new Set(Object.values(mapping).filter(Boolean));
  const caseAttributeColumns = headers.filter(h => !mappedColumns.has(h) && (columnRoles[h] || 'case-attribute') === 'case-attribute');
  const eventAttributeColumns = headers.filter(h => !mappedColumns.has(h) && columnRoles[h] === 'event-attribute');
  const cases = new Map();
  const events = [];
  const eventIdCounts = new Map();

  rows.forEach((row, rowIndex) => {
    const caseIdValue = nonEmpty(row?.[mapping.caseId]);
    const caseKeyValue = nonEmpty(row?.[mapping.caseKey], caseIdValue);
    const activityValue = nonEmpty(row?.[mapping.activity]);
    const timestampValue = normalizeTimestamp(nonEmpty(row?.[mapping.timestamp]));

    if (!caseIdValue || !activityValue || !timestampValue) {
      diagnostics.push({ severity: 'warning', code: 'row-skipped', message: `Row ${rowIndex + 2} skipped: case ID, activity and timestamp are required.` });
      return;
    }

    const caseId = String(caseIdValue);
    const caseKey = String(caseKeyValue || caseId);
    const caseAttrs = collectAttributeValues(row, caseAttributeColumns, 'ta');
    const existing = cases.get(caseId) || {
      id: caseId,
      key: caseKey,
      summary: String(nonEmpty(row?.[mapping.summary])),
      createdAt: normalizeTimestamp(nonEmpty(row?.[mapping.createdAt])),
      closedAt: normalizeTimestamp(nonEmpty(row?.[mapping.closedAt])),
      attributes: {},
      metrics: {},
      source: { nativeId: caseId, nativeKey: caseKey },
      extensions: {},
    };
    existing.attributes = { ...existing.attributes, ...caseAttrs };
    if (!existing.summary) existing.summary = String(nonEmpty(row?.[mapping.summary]));
    if (!existing.createdAt) existing.createdAt = normalizeTimestamp(nonEmpty(row?.[mapping.createdAt]));
    if (!existing.closedAt) existing.closedAt = normalizeTimestamp(nonEmpty(row?.[mapping.closedAt]));
    cases.set(caseId, existing);

    const baseEventId = `csv:${caseId}:${timestampValue}:${activityValue}`;
    const n = eventIdCounts.get(baseEventId) || 0;
    eventIdCounts.set(baseEventId, n + 1);
    events.push({
      id: n ? `${baseEventId}:${n}` : baseEventId,
      caseId,
      caseKey,
      activity: String(activityValue),
      activityType: String(model.activityType || model.activityLabel || 'Activity'),
      timestamp: timestampValue,
      resource: String(nonEmpty(row?.[mapping.resource], 'N/A')),
      attributes: collectAttributeValues(row, eventAttributeColumns, 'ea'),
      source: { rowNumber: rowIndex + 2, eventType: 'csv-event' },
      extensions: {},
    });
  });

  const caseList = Array.from(cases.values());
  if (caseList.length > maxCases) {
    diagnostics.push({ severity: 'error', code: 'case-limit-exceeded', message: `This model contains ${caseList.length.toLocaleString()} cases. Process Advisor supports a maximum of ${Number(maxCases).toLocaleString()} cases per model.` });
  }
  const caseById = new Map(caseList.map(c => [String(c.id), c]));
  events.sort((a,b) => String(a.caseKey).localeCompare(String(b.caseKey)) || String(a.timestamp).localeCompare(String(b.timestamp)) || String(a.id).localeCompare(String(b.id)));
  for (const event of events) {
    const c = caseById.get(String(event.caseId));
    if (!c) continue;
    if (!c.createdAt || String(event.timestamp) < String(c.createdAt)) c.createdAt = event.timestamp;
    if (!c.closedAt || String(event.timestamp) > String(c.closedAt)) c.closedAt = event.timestamp;
  }

  const dataset = createCanonicalDataset({
    platform: CANONICAL_PLATFORMS.MANUAL,
    model: { name: model.name || 'CSV Assessment', processType: model.processType || 'Imported Process', ...model },
    cases: caseList,
    events,
    caseAttributes: buildDefinitions(caseAttributeColumns, 'ta', attributeNotes),
    eventAttributes: buildDefinitions(eventAttributeColumns, 'ea', attributeNotes),
    metadata: { source: { type: INGESTION_SOURCE_TYPES.CSV_EVENT_LOG, filename: model.filename || '' }, csv: { mapping, columnRoles, attributeNotes } },
  });
  return createIngestionResult({ sourceType: INGESTION_SOURCE_TYPES.CSV_EVENT_LOG, dataset, diagnostics, metadata: { filename: model.filename || '', mapping, columnRoles, attributeNotes } });
}

export function ingestCsvWorkItemRows({ rows = [], headers = [], mapping = {}, columnRoles = {}, attributeNotes = {}, model = {}, maxCases = 50000 } = {}) {
  const diagnostics = [];
  if (!mapping.caseId) diagnostics.push({ severity: 'error', code: 'missing-mapping', message: 'Map a CSV column to caseId.' });
  if (!mapping.createdAt && !mapping.timestamp) diagnostics.push({ severity: 'error', code: 'missing-mapping', message: 'Map a created/opened timestamp for work-item import.' });

  const mappedColumns = new Set(Object.values(mapping).filter(Boolean));
  const caseAttributeColumns = headers.filter(h => !mappedColumns.has(h) && (columnRoles[h] || 'case-attribute') === 'case-attribute');
  const cases = [];
  const events = [];
  const seen = new Set();

  rows.forEach((row, rowIndex) => {
    const caseId = String(nonEmpty(row?.[mapping.caseId]));
    if (!caseId) { diagnostics.push({ severity: 'warning', code: 'row-skipped', message: `Row ${rowIndex + 2} skipped: case ID is required.` }); return; }
    if (seen.has(caseId)) { diagnostics.push({ severity: 'warning', code: 'duplicate-work-item', message: `Row ${rowIndex + 2} skipped: duplicate work item ${caseId}.` }); return; }
    seen.add(caseId);
    const caseKey = String(nonEmpty(row?.[mapping.caseKey], caseId));
    const createdAt = normalizeTimestamp(nonEmpty(row?.[mapping.createdAt], row?.[mapping.timestamp]));
    const closedAt = normalizeTimestamp(nonEmpty(row?.[mapping.closedAt]));
    const activity = String(nonEmpty(row?.[mapping.activity], 'Created'));
    const attrs = collectAttributeValues(row, caseAttributeColumns, 'ta');
    cases.push({ id: caseId, key: caseKey, summary: String(nonEmpty(row?.[mapping.summary])), createdAt, closedAt, attributes: attrs, metrics: {}, source: { nativeId: caseId, nativeKey: caseKey }, extensions: {} });
    if (createdAt) events.push({ id: `csv:${caseId}:${createdAt}:Created`, caseId, caseKey, activity: 'Created', activityType: String(model.activityType || model.activityLabel || 'Activity'), timestamp: createdAt, resource: String(nonEmpty(row?.[mapping.resource], 'N/A')), attributes: {}, source: { rowNumber: rowIndex + 2, eventType: 'csv-generated' }, extensions: {} });
    if (closedAt && closedAt !== createdAt) events.push({ id: `csv:${caseId}:${closedAt}:${activity}`, caseId, caseKey, activity: activity || 'Closed', activityType: String(model.activityType || model.activityLabel || 'Activity'), timestamp: closedAt, resource: String(nonEmpty(row?.[mapping.resource], 'N/A')), attributes: {}, source: { rowNumber: rowIndex + 2, eventType: 'csv-generated' }, extensions: {} });
  });

  if (cases.length > maxCases) {
    diagnostics.push({ severity: 'error', code: 'case-limit-exceeded', message: `This model contains ${cases.length.toLocaleString()} cases. Process Advisor supports a maximum of ${Number(maxCases).toLocaleString()} cases per model.` });
  }

  const dataset = createCanonicalDataset({
    platform: CANONICAL_PLATFORMS.MANUAL,
    model: { name: model.name || 'CSV Assessment', processType: model.processType || 'Imported Process', ...model },
    cases,
    events,
    caseAttributes: buildDefinitions(caseAttributeColumns, 'ta', attributeNotes),
    eventAttributes: [],
    metadata: { source: { type: INGESTION_SOURCE_TYPES.CSV_WORK_ITEMS, filename: model.filename || '' }, csv: { mapping, columnRoles, attributeNotes } },
  });
  return createIngestionResult({ sourceType: INGESTION_SOURCE_TYPES.CSV_WORK_ITEMS, dataset, diagnostics, metadata: { filename: model.filename || '', mapping, columnRoles, attributeNotes } });
}


export function createCsvSourceAdapter() {
  return defineSourceAdapter({
    type: SOURCE_ADAPTER_TYPES.CSV,
    async testConnection() {
      return { ok: true, connectionRequired: false };
    },
    async getSourceMetadata({ rows = [], headers = [], filename = '' } = {}) {
      return { filename, rowCount: rows.length, columns: [...headers] };
    },
    async getAvailableProcesses() {
      return [];
    },
    async getAvailableFields({ headers = [] } = {}) {
      return headers.map(name => ({ id: name, name, label: name, dataType: 'string' }));
    },
    async preview({ rows = [], limit = 20 } = {}) {
      return rows.slice(0, Math.max(1, Number(limit) || 20));
    },
    async extract({ mode = 'event-log', ...input } = {}) {
      return mode === 'work-items' ? ingestCsvWorkItemRows(input) : ingestCsvEventRows(input);
    },
    async refresh(input = {}) {
      return this.extract(input);
    },
  });
}
